import { Button, Divider, Drawer, IconButton, Stack, Typography, Box, Paper } from "@mui/material";
import { Form, Formik } from "formik";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { addTemplate, updateTemplate, uploadTemplateImage } from "@/features/admin/templates/slice";
import Details from "../../../features/newTemplate/components/Details";
import Config from "../../../features/newTemplate/components/Config";
import { Template } from "@/types";
import { HugeiconsIcon } from "@hugeicons/react";
import { Cancel01Icon } from "@hugeicons/core-free-icons";
import useSnackbar from "@/hooks/useSnackbar";
import LoadingButton from "@mui/lab/LoadingButton";

interface Props {
    open: boolean;
    onClose: () => void;
    templateToEdit?: Template | null;
}

export type FormValues = Partial<Template> & {
    imageFile?: File | null;
};

const TemplateDrawer = ({ open, onClose, templateToEdit }: Props) => {
    const dispatch = useAppDispatch();
    const user = useAppSelector((state) => state.auth.user);
    const [openSnackbar] = useSnackbar();

    const handleSubmit = async (values: FormValues) => {
        try {
            if (templateToEdit) {
                await dispatch(updateTemplate(values as Template)).unwrap();
                openSnackbar("Plantilla actualizada exitosamente", "success");
            } else {
                const result = await dispatch(addTemplate(values as any)).unwrap();
                const newTemplate = result as Template;

                if (values.imageFile && newTemplate?.id) {
                    try {
                        const uploadResult = await dispatch(
                            uploadTemplateImage({
                                templateId: newTemplate.id,
                                file: values.imageFile,
                            })
                        ).unwrap();

                        if (uploadResult && uploadResult.image) {
                            await dispatch(
                                updateTemplate({
                                    ...newTemplate,
                                    image: uploadResult.image,
                                })
                            ).unwrap();
                        }
                    } catch (uploadError) {
                        console.error("Error uploading image for new template:", uploadError);
                    }
                }
                openSnackbar("Plantilla creada exitosamente", "success");
            }
            onClose();
        } catch (error) {
            console.error(error);
            openSnackbar("Ocurrió un error al guardar la plantilla", "error");
        }
    };

    const initialValues: FormValues = templateToEdit
        ? {
            ...templateToEdit,
        }
        : {
            isPrivate: false,
            isPublished: false,
            createdBy: user?.uid ?? '',
            type: 'web',
            industry: [],
            imageFile: null,
        };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: 700,
                    maxWidth: "100%",
                },
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                padding={2}
            >
                <Typography variant="h2">
                    {templateToEdit ? "Actualizar Plantilla" : "Crear Plantilla"}
                </Typography>
                <Paper
                    sx={{
                        borderRadius: '50%',
                    }}
                >
                    <IconButton color="primary" onClick={onClose}>
                        <HugeiconsIcon icon={Cancel01Icon} />
                    </IconButton>
                </Paper>
            </Stack>
            <Divider />

            <Formik
                initialValues={initialValues}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {({ handleSubmit, isSubmitting }) => (
                    <Form style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <Box sx={{ padding: 3, flex: 1, overflowY: 'auto' }}>
                            <Stack spacing={3}>
                                <Details />
                                <Divider />
                                <Config />
                            </Stack>
                        </Box>

                        <Divider />

                        <Stack direction="row" spacing={2} justifyContent="flex-start" padding={3}>
                            <LoadingButton
                                variant="contained"
                                color="success"
                                onClick={() => handleSubmit()}
                                loading={isSubmitting}
                            >
                                {templateToEdit ? "Actualizar" : "Crear"}
                            </LoadingButton>
                            <Button variant="outlined" onClick={onClose}>
                                Cancelar
                            </Button>
                        </Stack>
                    </Form>
                )}
            </Formik>
        </Drawer>
    );
};

export default TemplateDrawer;
